/**
 * ToolCallBlock — renders one tool invocation as a "verb · object · glance" row
 *
 * @sync OpenCode → packages/opencode/src/cli/cmd/tui/routes/session/index.tsx (tool rendering)
 * @papan-only divergence: OpenCode renders tool calls as expandable
 *   input/output boxes; we render a per-tool row language with rich detail
 *   bodies (see ToolRenderers.tsx and the design doc §2). Data flow and
 *   status semantics stay identical — only presentation diverges.
 *
 * Row anatomy (ToolRow):
 *   [status/icon] Verb Object   glance · badges · duration  [chevron]
 *      └─ error line (red, first line — visible WITHOUT expanding)
 *      └─ expanded: tool-specific detail (terminal / DiffView / list / text)
 *
 * Special cases kept from the previous generation:
 *   • task while running → row + SubagentLiveStatus ticks
 *   • task completed → row + child-session conversation expand
 *   • permission-denied → strikethrough + Ban icon
 *   • elevated-model chip (task/run_batch_llm on a hotter model)
 *   • forceInline (display option "hide tool details") → collapsed by default,
 *     but still expandable on click (never auto-expands)
 */
import { useState, useCallback, useEffect, useRef } from 'react'
import {
  ChevronRight, ChevronDown, Loader2, CheckCircle2, XCircle, Ban, Flame,
} from 'lucide-react'
import { request } from '@papan/api-client'
import { useTranslation } from '@papan/i18n'
import { TOOL_DESCRIPTORS, genericDescriptor, TextDetailForError, type ToolRenderProps } from './ToolRenderers.js'
import { apiToolPartToRow, type ApiToolPartRow } from './tool-part-row.js'

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const s = ms / 1000
  return s < 10 ? `${s.toFixed(1)}s` : `${Math.round(s)}s`
}

/** Strip the `provider/` prefix so the chip can fit beside the title. */
function shortModelName(model: string): string {
  const slash = model.lastIndexOf('/')
  return slash === -1 ? model : model.slice(slash + 1)
}

/**
 * Chip shown when a `task` or `run_batch_llm` call ran on a strictly
 * higher-tier model than the parent's session model — i.e. when the user
 * is paying more than their session's base model implies for that call.
 *
 * Driven by `metadata.elevated` (populated server-side by Reasoning.resolve)
 * — NOT `clampedByFlag`. A clamped call is the cost-safe default; only an
 * actual escalation warrants the warning.
 */
function ElevatedModelChip({ metadata }: { metadata?: Record<string, unknown> }) {
  if (!metadata || metadata.elevated !== true) return null
  const model = typeof metadata.model === 'string' ? metadata.model : ''
  const requested = typeof metadata.requestedReasoning === 'string' ? metadata.requestedReasoning : ''
  return (
    <span
      title={`This call ran on a hotter model (${model || 'unknown'})${requested ? ` — requested reasoning: ${requested}` : ''}. Cost is higher than your session's base model.`}
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-500 shrink-0"
    >
      <Flame size={10} />
      <span className="tabular-nums">{shortModelName(model)}</span>
    </span>
  )
}

interface Props {
  toolName: string
  input: Record<string, unknown>
  output?: string
  error?: string
  status: 'pending' | 'running' | 'completed' | 'error'
  title?: string
  metadata?: Record<string, unknown>
  inputSize?: number
  wrapLines?: boolean
  /** Force all tools to render as rows without detail (display option "hide tool details") */
  forceInline?: boolean
  /** Workspace ID for subagent message fetching */
  workspaceId?: string
  /** Live status updates from the subagent (only for `task` tool while
   *  running). Populated by the SessionBus subscribeChild filter +
   *  reducer; see features/agent/ui/hooks.ts 'subagent-update' case. */
  subagentUpdates?: Array<{
    childSessionId: string
    kind: 'tool-start' | 'tool-done' | 'text' | 'completion' | 'error'
    summary: string
    detail?: string
    at: number
  }>
  /** Called when a /papan/ path in a tool row is clicked */
  onPathClick?: (path: string) => void
  /** Render inert — no control may act on the chat (a helper's step shown in
   *  the parent's expanded helper view). Opening a file stays allowed. */
  readOnly?: boolean
}

/** Per-tool pending/running messages — used by the cluster shimmer header. */
const RUNNING_LABELS: Record<string, string> = {
  bash: 'Running...',
  read_file: 'Reading...',
  write_file: 'Writing...',
  edit_file: 'Editing...',
  multiedit: 'Editing...',
  apply_patch: 'Patching...',
  glob: 'Searching...',
  grep: 'Searching...',
  list_files: 'Listing...',
  web_search: 'Searching...',
  webfetch: 'Fetching...',
  task: 'Running task...',
  skill: 'Loading skill...',
  file_version: 'Checking versions...',
  office_tool: 'Working on document...',
  libre_office_tool: 'Working on document...',
  render_ui: 'Rendering UI...',
  todowrite: 'Updating to-dos...',
  cred_to_env: 'Requesting key...',
  cred_used: 'Checking key...',
}

/** Did the user DENY this call? The tool wrapper records a denial as a COMPLETED
 *  result (the model is told "The user denied this action…"), so a status check
 *  alone rendered a denied write as "Wrote file.txt". Signals, strongest first:
 *  the wrapper's UI-only `metadata.denied` flag, the wrapper's fixed denial text
 *  (parts persisted before the flag existed), and a thrown denial's error text. */
const DENIAL_OUTPUT_PREFIX = 'The user denied this action'
function isDenied(error?: string, output?: string, metadata?: Record<string, unknown>): boolean {
  if (metadata?.denied === true) return true
  if (typeof output === 'string' && output.startsWith(DENIAL_OUTPUT_PREFIX)) return true
  if (!error) return false
  return error.includes('Permission denied') || error.includes('PermissionDenied')
}

/** Aggregate +/− line totals for a tool call, or null when it carries no
 *  diff. Used by ToolCluster to sum a badge across a grouped run. */
export function getToolDiffTotals(toolName: string, metadata?: Record<string, unknown>): {
  additions: number
  deletions: number
} | null {
  if (!metadata) return null
  if ((toolName === 'edit_file' || toolName === 'write_file') && metadata.filediff) {
    const fd = metadata.filediff as { additions: number; deletions: number }
    return { additions: fd.additions, deletions: fd.deletions }
  }
  if ((toolName === 'multiedit' || toolName === 'apply_patch') && Array.isArray(metadata.files)) {
    const files = metadata.files as Array<{ additions: number; deletions: number }>
    return {
      additions: files.reduce((s, f) => s + f.additions, 0),
      deletions: files.reduce((s, f) => s + f.deletions, 0),
    }
  }
  return null
}

/** One-line human description of a tool call — running label + title.
 *  Used by ToolCluster's shimmer header for the currently-running tool. */
export function describeToolCall(toolName: string, input: Record<string, unknown>, title?: string): string {
  const label = RUNNING_LABELS[toolName] || 'Running...'
  const desc = title || formatToolTitle(toolName, input)
  return desc && desc !== toolName ? `${label} ${desc}` : label
}

function formatToolTitle(toolName: string, input: Record<string, unknown>): string {
  switch (toolName) {
    case 'bash':
      return String(input.description || '') || `$ ${truncate(String(input.command || ''), 60)}`
    case 'read_file':
      return String(input.path || '')
    case 'write_file':
      return `Write ${input.path}`
    case 'edit_file':
    case 'multiedit':
      return `Edit ${input.path}`
    case 'apply_patch':
      return `Patch ${(input.paths as string[] || []).join(', ').slice(0, 60) || 'files'}`
    case 'glob':
    case 'grep':
      return String(input.pattern || '')
    case 'list_files':
      return String(input.path || '/')
    case 'web_search':
      return `Search: ${input.query}`
    case 'webfetch':
      return String(input.url || '')
    case 'task':
      return String(input.description || input.prompt || toolName)
    case 'skill':
      return `skill: ${input.name || 'loading...'}`
    default:
      return toolName
  }
}

function truncate(s: string, len: number): string {
  return s.length > len ? s.slice(0, len - 3) + '...' : s
}

function formatInputSize(bytes: number): string {
  return `${(bytes / 1024).toFixed(1)}K`
}

export function ToolCallBlock({ toolName, input, output, error, status, title, metadata, inputSize, wrapLines, forceInline, workspaceId, subagentUpdates, onPathClick, readOnly }: Props) {
  const denied = isDenied(error, output, metadata)

  // Task tool with completed status — child-session expandable view.
  if (toolName === 'task' && status === 'completed' && workspaceId) {
    return <SubagentExpandBlock input={input} output={output} title={title} metadata={metadata} workspaceId={workspaceId} onPathClick={onPathClick} />
  }

  const p: ToolRenderProps = { toolName, input, output, error, status, title, metadata, wrapLines, onPathClick, readOnly }
  const descriptor = TOOL_DESCRIPTORS[toolName] || genericDescriptor

  // Task tool while running — row + live status ticks from the child.
  if (toolName === 'task' && (status === 'running' || status === 'pending') && subagentUpdates && subagentUpdates.length > 0) {
    return (
      <div>
        <ToolRow p={p} descriptor={descriptor} denied={denied} inputSize={inputSize} forceInline={forceInline} />
        <SubagentLiveStatus updates={subagentUpdates} />
      </div>
    )
  }

  return <ToolRow p={p} descriptor={descriptor} denied={denied} inputSize={inputSize} forceInline={forceInline} />
}

/* ─── the row primitive ─────────────────────────────────────────────── */

function ToolRow({ p, descriptor, denied, inputSize, forceInline }: {
  p: ToolRenderProps
  descriptor: typeof genericDescriptor
  denied: boolean
  inputSize?: number
  forceInline?: boolean
}) {
  const [expanded, setExpanded] = useState(
    () => !forceInline && !!descriptor.defaultExpanded?.(p),
  )
  // defaultExpanded can flip false→true AFTER mount (the surface payload
  // arrives with tool completion metadata) — auto-open then, but never
  // fight a user who explicitly collapsed.
  const userToggledRef = useRef(false)
  const shouldAutoOpen = !forceInline && !!descriptor.defaultExpanded?.(p)
  useEffect(() => {
    if (shouldAutoOpen && !userToggledRef.current) setExpanded(true)
  }, [shouldAutoOpen])
  const Icon = descriptor.icon
  const isPending = p.status === 'pending'
  const isRunning = p.status === 'running' || isPending

  const rawSpec = descriptor.row(p)
  // Say what HAPPENED, not only the phase: a denied call reads "Denied" (never the
  // descriptor's success verb — a denied write is recorded as a completed result),
  // and a finished-but-failed call never keeps an in-progress "…ing" verb (a
  // not-performed write read "Writing file.txt" forever). Descriptors that already
  // choose a past-tense verb for errors (bash's "Ran") are left alone.
  const spec = denied
    ? { ...rawSpec, verb: 'Denied' }
    : p.status === 'error' && /ing$/.test(String(rawSpec.verb))
    ? { ...rawSpec, verb: 'Failed' }
    : rawSpec
  // Detail body: tool-specific; when the tool failed and its descriptor has
  // no error-bearing body (read_file, glob…), fall back to the error text.
  // "Tool details" OFF (forceInline) means collapsed BY DEFAULT — not
  // un-expandable. Keep the detail available so the row stays click-to-expand
  // (consistent with the live-output expand while a command runs). Auto-expand
  // stays suppressed under forceInline via the useState/shouldAutoOpen gates above.
  const detail = descriptor.detail?.(p) ?? (p.error && !denied ? <TextDetailForError error={p.error} wrapLines={p.wrapLines} /> : null)

  // Live streaming output while a command runs (Papan extension). Collapsed by
  // default it shows the original compact last-lines preview; expand the row to
  // inspect the full command (input) + full output — same compact 10px style.
  const liveCommand = typeof (p.input as any)?.command === 'string' ? (p.input as any).command as string : undefined
  const liveOutput = isRunning && typeof p.metadata?.liveOutput === 'string' ? (p.metadata.liveOutput as string) : ''
  const hasLive = isRunning && (!!liveOutput.trim() || !!liveCommand)

  const expandable = (!!detail && !isRunning) || hasLive

  const statusIcon = isRunning
    ? <Loader2 size={11} className="animate-spin text-blue-400 shrink-0" />
    : denied
    ? <Ban size={11} className="text-red-400 shrink-0" />
    : p.status === 'error'
    ? <XCircle size={11} className="text-red-400 shrink-0" />
    : <Icon size={11} className="themed-text-muted shrink-0" />

  const durationMs = typeof p.metadata?.durationMs === 'number' ? p.metadata.durationMs : undefined

  const row = (
    <>
      {statusIcon}
      <span className={`min-w-0 flex items-baseline gap-1.5 text-xs ${denied ? 'line-through' : ''} ${isRunning ? 'chat-shimmer' : ''}`}>
        <span className={`shrink-0 ${isRunning ? '' : 'themed-text-secondary'}`}>{spec.verb}</span>
        {isPending && !spec.object
          ? (inputSize ? <span className="themed-text-muted">{formatInputSize(inputSize)}</span> : null)
          : spec.object && <span className="min-w-0 truncate themed-text">{spec.object}</span>}
      </span>
      {!isRunning && spec.glance != null && (
        <span className="text-[11px] themed-text-muted shrink-0 truncate max-w-[40%]">{spec.glance}</span>
      )}
      <ElevatedModelChip metadata={p.metadata} />
      {!isRunning && durationMs != null && (
        <span
          className="text-[10px] themed-text-muted opacity-0 group-hover/tool:opacity-100 transition-opacity tabular-nums shrink-0"
          title={`Execution time: ${formatDuration(durationMs)}`}
        >
          {formatDuration(durationMs)}
        </span>
      )}
      {expandable && (
        expanded
          ? <ChevronDown size={11} className="themed-text-muted shrink-0" />
          : <ChevronRight size={11} className="themed-text-muted opacity-0 group-hover/tool:opacity-100 transition-opacity shrink-0" />
      )}
    </>
  )

  return (
    <div className={`group/tool ${denied ? 'opacity-50' : ''}`}>
      {/* gap-2 (8px) + 11px icon = the 19px detail/error indent below */}
      {expandable ? (
        // A DIV (role=button), NOT a <button>: `row` can contain a FilePill,
        // which is itself a <button>, and a button-in-button is invalid HTML —
        // it throws a React hydration error that makes React drop this subtree,
        // so the conversation renders only partially. A role=button div holds
        // interactive children legally while staying click/keyboard-accessible.
        <div
          role="button"
          tabIndex={0}
          onClick={() => { userToggledRef.current = true; setExpanded(v => !v) }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              userToggledRef.current = true
              setExpanded(v => !v)
            }
          }}
          className="w-full flex items-center gap-2 py-1 text-left hover:opacity-90 transition-opacity cursor-pointer"
        >
          {row}
        </div>
      ) : (
        <div className="flex items-center gap-2 py-1">{row}</div>
      )}

      {/* Error first line — visible WITHOUT expanding (denied is conveyed
          by the strikethrough + Ban, no need to repeat it) */}
      {p.status === 'error' && p.error && !denied && (
        <div className="pl-[19px] text-[11px] text-red-400/90 truncate">
          {p.error.split('\n')[0]}
        </div>
      )}

      {/* Live streaming output while a command runs (Papan extension). Collapsed:
          the original compact last-8-lines preview. Expanded (click the row):
          full command + full output, same compact style, scrollable. */}
      {hasLive && !expanded && liveOutput.trim() && (
        <pre className="pl-[19px] mt-0.5 text-[10px] leading-snug themed-text-muted font-mono whitespace-pre-wrap break-all max-h-24 overflow-hidden">
          {liveOutput.split('\n').slice(-8).join('\n')}
        </pre>
      )}
      {hasLive && expanded && (
        <div className="pl-[19px] mt-0.5 space-y-0.5">
          {liveCommand && (
            <pre className="text-[10px] leading-snug themed-text-secondary font-mono whitespace-pre-wrap break-all">$ {liveCommand}</pre>
          )}
          {liveOutput.trim() && (
            <pre className="text-[10px] leading-snug themed-text-muted font-mono whitespace-pre-wrap break-all max-h-60 overflow-y-auto">
              {liveOutput}
            </pre>
          )}
        </div>
      )}

      {expanded && detail && <div className="pl-[19px] py-1">{detail}</div>}
    </div>
  )
}

/** Live status list shown under a running task tool-call. Each item is
 *  one forwarded child event (tool-start, tool-done, error, completion).
 *  Kept intentionally small + dimmed — these are diagnostic ticks, not
 *  the primary content. */
function SubagentLiveStatus({ updates }: {
  updates: Array<{
    childSessionId: string
    kind: 'tool-start' | 'tool-done' | 'text' | 'completion' | 'error'
    summary: string
    detail?: string
    at: number
  }>
}) {
  const ICONS: Record<string, string> = {
    'tool-start': '▸',
    'tool-done': '✓',
    'text': '·',
    'completion': '✓',
    'error': '✗',
  }
  return (
    <div className="mt-1 ml-5 pl-2 border-l-2 border-brand-500/20 space-y-0.5">
      {updates.map((u, i) => (
        <div
          key={`${u.at}_${i}`}
          className={`text-[10px] themed-text-muted ${u.kind === 'error' ? 'text-red-400/80' : ''}`}
        >
          <span className="font-mono mr-1">{ICONS[u.kind] ?? '·'}</span>
          <span>{u.summary}</span>
        </div>
      ))}
    </div>
  )
}

/** One step of the helper's transcript: a run of text, or one tool call. */
type ChildSegment = { kind: 'text'; text: string } | ({ kind: 'tool' } & ApiToolPartRow)
type ChildMessage = { role: string; segments: ChildSegment[] }

/** The expanded helper view shows at most this many messages, and this many
 *  steps (text runs + tool rows) across them. The step cap is what bounds a
 *  long helper: the worker writes ONE assistant message per child run, so the
 *  message cap alone never limited how many tool rows mount. */
const HELPER_MAX_MESSAGES = 10
const HELPER_MAX_STEPS = 30

/** The first HELPER_MAX_MESSAGES messages, their segments cut at
 *  HELPER_MAX_STEPS in order; `hiddenSteps` counts what the cut left out of
 *  those messages (messages beyond the cap have their own note). */
function capHelperTranscript(msgs: ChildMessage[]): { shown: ChildMessage[]; hiddenSteps: number } {
  let budget = HELPER_MAX_STEPS
  let hiddenSteps = 0
  const shown: ChildMessage[] = []
  for (const m of msgs.slice(0, HELPER_MAX_MESSAGES)) {
    const take = Math.min(budget, m.segments.length)
    hiddenSteps += m.segments.length - take
    budget -= take
    if (take > 0) shown.push({ role: m.role, segments: m.segments.slice(0, take) })
  }
  return { shown, hiddenSteps }
}

/** The child session's stored messages → what the expanded helper view shows,
 *  in order: text (consecutive text parts joined, as before) and tool parts.
 *  Reasoning, step and every other part stay hidden. A message with nothing to
 *  show is dropped, so a text-only child renders exactly as it did. Tool parts
 *  map through `apiToolPartToRow`, the same mapping the main transcript uses. */
function toChildMessages(
  msgs: Array<{ info: { role: string }; parts: Array<Record<string, any>> }>,
): ChildMessage[] {
  return msgs.map(m => {
    const segments: ChildSegment[] = []
    for (const p of m.parts) {
      if (p.type === 'text') {
        const text = p.text || p.content || ''
        const last = segments[segments.length - 1]
        if (last && last.kind === 'text') last.text += text
        else segments.push({ kind: 'text', text })
      } else if (p.type === 'tool') {
        segments.push({ kind: 'tool', ...apiToolPartToRow(p) })
      }
    }
    return { role: m.info.role, segments: segments.filter(s => s.kind !== 'text' || s.text) }
  }).filter(m => m.segments.length > 0)
}

/** Subagent expandable block — "Delegated <task>" row that expands to the
 *  child session's conversation: its text and its tool steps (#839). */
function SubagentExpandBlock({
  input, output, title, metadata, workspaceId, onPathClick,
}: {
  input: Record<string, unknown>
  output?: string
  title?: string
  metadata?: Record<string, unknown>
  workspaceId: string
  onPathClick?: (path: string) => void
}) {
  const { t } = useTranslation('agent')
  const [expanded, setExpanded] = useState(false)
  const [childMessages, setChildMessages] = useState<ChildMessage[] | null>(null)
  const [loading, setLoading] = useState(false)

  const displayTitle = title || String(input.description || input.prompt || 'Task')

  // Child session ID lives in metadata, not input — the task tool emits
  // it via ctx.metadata({sessionId: childSessionId}) and the worker's
  // tool-call-done enrichment attaches the metadata blob. Reading from
  // input.sessionId (pre-yield-port) was a pre-existing bug — the input
  // schema is {description, prompt, agent, tools?, reasoning?} with no
  // sessionId field. With Day 10b-d this is now a REAL session row in
  // the DB so /api/agent/sessions/<childId>/message returns its messages.
  const childSessionId = metadata?.sessionId as string | undefined

  const loadChildMessages = useCallback(async () => {
    if (!childSessionId || childMessages) return
    setLoading(true)
    try {
      const msgs = await request<Array<{ info: { role: string }; parts: Array<Record<string, any>> }>>(
        `/api/agent/sessions/${childSessionId}/message?workspaceId=${encodeURIComponent(workspaceId)}`,
      )
      setChildMessages(toChildMessages(msgs))
    } catch {
      setChildMessages([])
    } finally {
      setLoading(false)
    }
  }, [childSessionId, childMessages, workspaceId])

  const capped = childMessages ? capHelperTranscript(childMessages) : null

  const handleExpand = () => {
    const next = !expanded
    setExpanded(next)
    if (next && childSessionId) loadChildMessages()
  }

  return (
    <div className="group/tool">
      <button
        type="button"
        onClick={handleExpand}
        className="w-full flex items-center gap-2 py-1 text-left hover:opacity-90 transition-opacity"
      >
        <CheckCircle2 size={11} className="text-green-400 shrink-0" />
        <span className="min-w-0 flex items-baseline gap-1.5 text-xs">
          <span className="shrink-0 themed-text-secondary">Delegated</span>
          <span className="min-w-0 truncate themed-text">{displayTitle}</span>
        </span>
        <ElevatedModelChip metadata={metadata} />
        {expanded
          ? <ChevronDown size={11} className="themed-text-muted shrink-0" />
          : <ChevronRight size={11} className="themed-text-muted opacity-0 group-hover/tool:opacity-100 transition-opacity shrink-0" />}
      </button>

      {expanded && (
        <div className="pl-[19px] py-1 space-y-2">
          {/* Task output summary */}
          {output && (
            <pre className="p-2 rounded-md text-xs font-mono themed-text-secondary max-h-[150px] overflow-y-auto bg-black/15 whitespace-pre-wrap break-words">
              {output.length > 500 ? output.slice(0, 500) + '...' : output}
            </pre>
          )}

          {/* Child session messages (if available) */}
          {loading && (
            <div className="flex items-center gap-2 text-xs themed-text-muted py-1">
              <Loader2 size={12} className="animate-spin" />
              <span>{t('toolCall.helperLoading')}</span>
            </div>
          )}

          {childMessages && childMessages.length > 0 && (
            <div className="border-l-2 border-brand-500/20 pl-2 space-y-1.5">
              <span className="text-[10px] uppercase themed-text-muted">{t('toolCall.helperConversation')}</span>
              {capped!.shown.map((msg, i) => (
                <div key={i} className="space-y-0.5">
                  {msg.segments.map((seg, j) => seg.kind === 'text' ? (
                    <div key={j} className={`text-xs ${msg.role === 'user' ? 'text-brand-300/80' : 'themed-text-muted'}`}>
                      <span className="font-medium">{msg.role === 'user' ? t('toolCall.helperUser') : t('toolCall.helperAgent')}: </span>
                      <span>{seg.text.length > 200 ? seg.text.slice(0, 200) + '...' : seg.text}</span>
                    </div>
                  ) : (
                    // The main chat's own row, so status and verb (running,
                    // completed, Failed, Denied) read the same. No workspaceId
                    // and no live updates: a nested `task` is a plain row, never
                    // another expandable helper (non-recursive by design).
                    // readOnly: nothing here may act on the PARENT chat (a
                    // render_ui surface is frozen); file pills still open.
                    <ToolCallBlock
                      key={j}
                      toolName={seg.toolName}
                      input={seg.input}
                      output={seg.output}
                      error={seg.error}
                      status={seg.status}
                      title={seg.title}
                      metadata={seg.metadata}
                      forceInline
                      readOnly
                      onPathClick={onPathClick}
                    />
                  ))}
                </div>
              ))}
              {capped!.hiddenSteps > 0 && (
                <span className="block text-[10px] themed-text-muted">{t('toolCall.helperMoreSteps', { count: capped!.hiddenSteps })}</span>
              )}
              {childMessages.length > HELPER_MAX_MESSAGES && (
                <span className="text-[10px] themed-text-muted">{t('toolCall.helperMoreMessages', { count: childMessages.length - HELPER_MAX_MESSAGES })}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
